import type { GridApi, GridRowOrderChangeParams } from '@mui/x-data-grid-pro';
import { GRID_ROOT_GROUP_ID, gridRowTreeSelector } from '@mui/x-data-grid-pro';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@tempo-io/sui-sonner';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ItemsApi } from '@/lib/api/common/items';
import type { ItemTreeSet, ItemTreeSetEntry } from '@/lib/api/types/items';
import { commonQueryKeys } from '@/lib/query-keys/common';
import { bumpVisibleItemsCache } from '../context/VisibleItemsContext';

// Index just past rootId's subtree in the flat DFS array (rootId's own index + all descendants).
const subtreeEndIndex = (
  entries: readonly ItemTreeSetEntry[],
  byId: ReadonlyMap<number, ItemTreeSetEntry>,
  startIdx: number,
  rootId: number,
): number => {
  let end = startIdx + 1;
  while (end < entries.length) {
    let cur: ItemTreeSetEntry | undefined = entries[end];
    let isDescendant = false;
    while (cur?.parentId != null) {
      if (cur.parentId === rootId) {
        isDescendant = true;
        break;
      }
      cur = byId.get(cur.parentId);
    }
    if (!isDescendant) break;
    end++;
  }
  return end;
};

// Pure reorder of the flat DFS array: moves movedId's subtree next to targetSiblingId.
// Returns the original array unchanged if the move would cross parents or an id is missing.
export const reorderTreeSetData = (
  entries: readonly ItemTreeSetEntry[],
  movedId: number,
  targetSiblingId: number,
  position: 'above' | 'below',
): readonly ItemTreeSetEntry[] => {
  const movedIdx = entries.findIndex((e) => e.id === movedId);
  if (movedIdx === -1) return entries;

  const byId = new Map(entries.map((e) => [e.id, e]));
  const movedParentId = entries[movedIdx]?.parentId ?? null;
  const subtreeEnd = subtreeEndIndex(entries, byId, movedIdx, movedId);
  const subtree = entries.slice(movedIdx, subtreeEnd);
  const withoutSubtree = [...entries.slice(0, movedIdx), ...entries.slice(subtreeEnd)];

  const targetIdx = withoutSubtree.findIndex((e) => e.id === targetSiblingId);
  if (targetIdx === -1) return entries;
  if ((withoutSubtree[targetIdx]?.parentId ?? null) !== movedParentId) return entries;

  const insertAt =
    position === 'above'
      ? targetIdx
      : subtreeEndIndex(withoutSubtree, byId, targetIdx, targetSiblingId);

  return [...withoutSubtree.slice(0, insertAt), ...subtree, ...withoutSubtree.slice(insertAt)];
};

// Optimistically applies reorderTreeSetData to priority-ordered tree-set queries
// only. Key shape: ['itemsTreeSet', filter, order, ...]; a falsy order at index 2
// means priority order — sorted variants keep their server order untouched.
const isPriorityOrderedKey = (queryKey: readonly unknown[]): boolean => !queryKey[2];

const reorderInCache = (
  queryClient: QueryClient,
  movedId: number,
  targetSiblingId: number,
  position: 'above' | 'below',
) => {
  queryClient.setQueriesData<ItemTreeSet>(
    {
      queryKey: commonQueryKeys.itemsTreeSet.all(),
      predicate: (query) => isPriorityOrderedKey(query.queryKey),
    },
    (cached) => {
      if (!cached) return cached;
      const data = reorderTreeSetData(cached.data, movedId, targetSiblingId, position);
      if (data === cached.data) return cached;
      return { ...cached, data: [...data] };
    },
  );
};

export const useItemPriorityReorder = (apiRef: React.RefObject<GridApi>, instanceKey: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return React.useCallback(
    async (params: GridRowOrderChangeParams) => {
      const tree = gridRowTreeSelector(apiRef);
      const parentId = params.newParent ?? GRID_ROOT_GROUP_ID;
      const parentNode = tree[parentId];
      if (!parentNode || !('children' in parentNode)) return;

      const { children } = parentNode;
      const idx = params.targetIndex;
      const previousItemId = idx > 0 ? Number(children[idx - 1]) : undefined;
      const nextItemId = idx < children.length - 1 ? Number(children[idx + 1]) : undefined;

      let relativePriority: { id: number; type: 'before' | 'after' } | undefined;
      if (previousItemId !== undefined) {
        relativePriority = { id: previousItemId, type: 'after' };
      } else if (nextItemId !== undefined) {
        relativePriority = { id: nextItemId, type: 'before' };
      } else {
        return;
      }

      // setRowPosition already repainted the grid; defer the cache reconcile
      // past the drop frame so its full-tree rebuild doesn't race that repaint.
      const anchorId = relativePriority.id;
      const cachePosition = relativePriority.type === 'after' ? 'below' : 'above';
      const reconcileHandle = requestAnimationFrame(() => {
        reorderInCache(queryClient, params.row.id as number, anchorId, cachePosition);
      });

      try {
        await ItemsApi.updateItem({
          itemId: params.row.id as number,
          data: { relativePriority },
        });
      } catch {
        // Cancel the forward reconcile in case it hasn't fired yet, so the
        // rollback below is the last write to the cache.
        cancelAnimationFrame(reconcileHandle);
        toast.error(
          t(($) => $.common.errors.global.title),
          {
            description: t(($) => $.common.errors.genericWithSupport),
          },
        );

        const api = apiRef.current;
        if (!api) return;

        const oldParentId = params.oldParent ?? GRID_ROOT_GROUP_ID;
        const oldParentNode = gridRowTreeSelector(apiRef)[oldParentId];
        if (!oldParentNode || !('children' in oldParentNode)) return;

        const sourceId = params.row.id as string | number;
        const siblings = oldParentNode.children.filter((id) => String(id) !== String(sourceId));
        const anchor = params.oldIndex === 0 ? siblings[0] : siblings[params.oldIndex - 1];
        const position = params.oldIndex === 0 ? 'above' : 'below';

        if (anchor === undefined) return;

        api.setRowPosition(sourceId, anchor, position);
        reorderInCache(queryClient, params.row.id as number, Number(anchor), position);
        bumpVisibleItemsCache(queryClient, instanceKey);
        return;
      }

      void queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsTreeSet.all() });
    },
    [apiRef, queryClient, t, instanceKey],
  );
};
